// eslint-disable-next-line no-unused-vars
import React, {useState, useEffect} from 'react';
import {getCoupons, createCoupon, deleteCoupon} from '../../api/axios';
import styled from 'styled-components';


const Container = styled.div`
  padding: 20px;
`;

const Form = styled.form`
  margin-bottom: 30px;
  padding: 15px;
  border: 1px solid #ccc;
  border-radius: 8px;

  div {
    margin-bottom: 10px;
  }

  label {
    margin-right: 10px;
  }

  input,
  select,
  textarea {
    padding: 5px;
    border-radius: 4px;
    border: 1px solid #aaa;
  }

  button {
    padding: 8px 15px;
    border: none;
    border-radius: 4px;
    background-color: #0077cc;
    color: #fff;
    cursor: pointer;
    margin-top: 5px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 10px;
    border: 1px solid #ddd;
    text-align: center;
  }

  th {
    background-color: #f4f4f4;
  }

  button {
    padding: 5px 10px;
    border: none;
    border-radius: 4px;
    background-color: #cc0000;
    color: #fff;
    cursor: pointer;
  }
`;

const SelectedItemsContainer = styled.div`
  margin-top: 10px;
  padding: 10px;
  border: 1px dashed #aaa;
  border-radius: 5px;
`;

const CouponManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Form data: note we removed discount_percent in favor of arcanium for discount coupons.
  const [formData, setFormData] = useState({
    code: '',
    type: 'item', // Default is Item-Coupon; alternative is "arcanium"
    arcanium: '',
    max_uses: '',
    account_bound: '',
    note: '',
  });

  // Instead of manually inputting item data, we use modal selection.
  const [selectedItems, setSelectedItems] = useState([]);
  // For account binding, we store the selected account object.
  const [selectedAccount, setSelectedAccount] = useState(null);


  // Fetch coupons from backend
  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const response = await getCoupons();
      setCoupons(response.data);
    } catch (err) {
      setError('Fehler beim Abrufen der Coupons.');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  // Update form fields
  const handleChange = e => {
    const {name, value} = e.target;
    setFormData(prev => ({...prev, [name]: value}));
  };

  // Compute expiration date: one week from now at 23:59:59
  const computeExpirationDate = () => {
    const now = new Date();
    const expiration = new Date(now);
    expiration.setDate(expiration.getDate() + 7);
    expiration.setHours(23, 59, 59, 0);
    return expiration.toISOString().slice(0, 19);
  };

  // Handle form submission to create a coupon
  const handleSubmit = async e => {
    e.preventDefault();
    const expiration_date = computeExpirationDate();
    let payload = {...formData, expiration_date};

    // When type is item, add selectedItems to payload
    if (formData.type === 'item') {
      payload.items = selectedItems;
      // Remove arcanium if present
      delete payload.arcanium;
    } else if (formData.type === 'arcanium') {
      // Remove items if any
      delete payload.items;
    }

    // If an account has been selected, store its id
    if (selectedAccount) {
      payload.account_bound = selectedAccount.id;
    }

    try {
      await createCoupon(payload);
      fetchCoupons();
      // Reset form, selected items, and account
      setFormData({
        code: '',
        type: 'item',
        arcanium: '',
        max_uses: '',
        account_bound: '',
        note: '',
      });
      setSelectedItems([]);
      setSelectedAccount(null);
    } catch (err) {
      setError('Fehler beim Erstellen des Coupons.');
    }
  };

  // Handle coupon deletion
  const handleDelete = async id => {
    try {
      await deleteCoupon(id);
      fetchCoupons();
    } catch (err) {
      setError('Fehler beim Löschen des Coupons.');
    }
  };

  return (
    <Container>
      <h2>Coupon Management</h2>
      {error && <p style={{color: 'red'}}>{error}</p>}

      {/* Form for creating a new coupon */}
      <Form onSubmit={handleSubmit}>
        <div>
          <label>Code:</label>
          <input type="text" name="code" value={formData.code} onChange={handleChange} required />
        </div>
        <div>
          <label>Typ:</label>
          <select name="type" value={formData.type} onChange={handleChange}>
            <option value="item">Item-Coupon</option>
            <option value="arcanium">Arcanium-Coupon</option>
          </select>
        </div>
        {formData.type === 'item' && (
          <div>
            <button type="button" >
              Items auswählen
            </button>
            {selectedItems.length > 0 && (
              <SelectedItemsContainer>
                <p>Ausgewählte Items:</p>
                {selectedItems.map(item => (
                  <p key={item.id}>
                    {item.item_name} (ID: {item.id})
                  </p>
                ))}
              </SelectedItemsContainer>
            )}
          </div>
        )}
        {formData.type === 'arcanium' && (
          <div>
            <label>Arcanium Wert:</label>
            <input type="number" name="arcanium" value={formData.arcanium} onChange={handleChange} required />
          </div>
        )}
        <div>
          <label>Max. Nutzungen (leer = unbegrenzt):</label>
          <input type="number" name="max_uses" value={formData.max_uses} onChange={handleChange} />
        </div>
        <div>
          <label>Account gebunden (leer = für alle Nutzer):</label>
          <input
            type="text"
            readOnly
            value={selectedAccount ? selectedAccount.username : ''}
            placeholder="Klicke um einen Account auszuwählen"
          />
        </div>
        <div>
          <label>Notiz:</label>
          <textarea name="note" value={formData.note} onChange={handleChange} />
        </div>
        <button type="submit">Coupon erstellen</button>
      </Form>

      {/* Table of existing coupons */}
      <h3>Existierende Coupons</h3>
      {loading ? (
        <p>Lade Coupons...</p>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Code</th>
              <th>Typ</th>
              <th>Arcanium</th>
              <th>Max. Nutzungen</th>
              <th>Verwendet</th>
              <th>Ablauf</th>
              <th>Notiz</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {coupons.map(coupon => (
              <tr key={coupon.id}>
                <td>{coupon.id}</td>
                <td>{coupon.code}</td>
                <td>{coupon.type}</td>
                <td>{coupon.type === 'arcanium' ? coupon.arcanium : coupon.type === 'item' ? 'Item Coupon' : '-'}</td>
                <td>{coupon.max_uses || 'Unbegrenzt'}</td>
                <td>{coupon.uses_count}</td>
                <td>{new Date(coupon.expiration_date).toLocaleString()}</td>
                <td>{coupon.note}</td>
                <td>
                  <button onClick={() => handleDelete(coupon.id)}>Löschen</button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

    </Container>
  );
};

export default CouponManagement;
